import { Dispatch, SetStateAction } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsRadioList, Typography } from "@foodpilot/foods";

import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { useDistributionReference } from "src/api/sure-api/useDistributionReference";
import type { DistributionReference } from "src/api/sure-api/useDistributionReference";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useDistributions, Distribution } from "src/api/client-api/distribution/useDistributions";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";

type Step7DistributionProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
};

/**
 * Step 7 - Distribution (Figma empty `12819:67580`, filled `13162:67953`,
 * gated by `FS-product-distribution`).
 *
 * Single-select radio group rendered inline (no `FormBox` / `h3` wrapper) — the
 * `SectionHeader` provided by `ProductFormV2` already carries the dynamic step title
 * and score badge. Choices come from `useDistributionReference`
 * (sure-api `distribution_references`) and write back to
 * `product.distributionReferenceId`.
 */
export const Step7Distribution = (props: Step7DistributionProps) => {
    const { product, setProduct } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const isManagedDistributionEnabled = isFeatureActivated("FS-manage-my-distribution");
    const { distributionReference, isLoading } = useDistributionReference();
    const { distributions, isLoading: isDistributionsLoading } = useDistributions({
        enabled: isManagedDistributionEnabled,
    });

    const radioListProps = {
        FormControlProps: {
            sx: {
                flexDirection: "row",
                flexWrap: "wrap",
                gap: theme.spacing(4),
            },
        },
        FormControlLabelProps: {
            sx: {
                marginLeft: 0,
                gap: theme.spacing(1.5),
                ".MuiTypography-root": {
                    color: theme.custom.grey[2500],
                },
            },
        },
        RadioProps: {
            sx: {
                padding: 0,
            },
        },
    };
    const bodySx = {
        display: "flex",
        padding: theme.spacing(3),
    } as const;

    if (isLoading || isDistributionsLoading) {
        return (
            <Box sx={{ ...bodySx, justifyContent: "center" }}>
                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
            </Box>
        );
    }

    const emptyState = (
        <Box sx={bodySx}>
            <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                {t("product.distribution.no_methods_available", "No distribution methods available")}
            </Typography>
        </Box>
    );

    if (isManagedDistributionEnabled) {
        if (distributions.length === 0) {
            return emptyState;
        }

        const selectedDistribution =
            distributions.find((distribution) => distribution.id === product.distribution?.id) ?? undefined;

        return (
            <Box sx={bodySx}>
                <FoodsRadioList
                    values={distributions}
                    selectedItem={selectedDistribution}
                    onChange={(distribution: Distribution) => {
                        setProduct((prev) => ({ ...prev, distribution, distributionReferenceId: null }));
                    }}
                    getId={(distribution) => distribution.id}
                    getName={(distribution) => distribution.name}
                    {...radioListProps}
                />
            </Box>
        );
    }

    if (distributionReference === undefined || distributionReference.length === 0) {
        return emptyState;
    }

    const selectedReference =
        distributionReference.find((distRef) => distRef.id === product.distributionReferenceId) ?? undefined;

    return (
        <Box sx={bodySx}>
            <FoodsRadioList
                values={distributionReference}
                selectedItem={selectedReference}
                onChange={(distRef: DistributionReference) => {
                    setProduct((prev) => ({ ...prev, distribution: null, distributionReferenceId: distRef.id }));
                }}
                getId={(distRef) => distRef.id}
                getName={(distRef) => distRef.label}
                {...radioListProps}
            />
        </Box>
    );
};
